'use client';

import { useFestivalContext } from '@/contexts/FestivalContext';
import { PathEnum } from '@/i18n/locale-config';
import { useLocale } from 'next-intl';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { usePathname as useNextPathname, useRouter as useNextRouter } from 'next/navigation';
import { AnchorHTMLAttributes, forwardRef, useCallback, useMemo } from 'react';
import type { UrlObject } from 'url';

type Href = string | UrlObject;

const isExternalHref = (href: string) =>
    /^(?:https?:|mailto:|tel:)/.test(href) || href.startsWith('#');

const serializeQuery = (query: UrlObject['query']) => {
    if (!query) return '';

    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
        if (value === undefined || value === null) {
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item !== undefined && item !== null) {
                    params.append(key, String(item));
                }
            });
            return;
        }

        params.append(key, String(value));
    });

    return params.toString();
};

const hrefToString = (href: Href): string => {
    if (typeof href === 'string') {
        return href;
    }

    const pathname = href.pathname ?? '';
    const queryString = serializeQuery(href.query);
    const hash =
        typeof href.hash === 'string' && href.hash.length > 0
            ? href.hash.startsWith('#')
                ? href.hash
                : `#${href.hash}`
            : '';

    return `${pathname ?? ''}${queryString ? `?${queryString}` : ''}${hash}`;
};

const normalizePath = (path: string) => {
    if (!path) return '/';
    return path.startsWith('/') ? path : `/${path}`;
};

const resolveEnumPath = (input: string): string => {
    const queryIndex = input.indexOf('?');
    const hashIndex = input.indexOf('#');

    const cutoffCandidates = [queryIndex, hashIndex].filter((index) => index >= 0);
    const cutoff = cutoffCandidates.length > 0 ? Math.min(...cutoffCandidates) : input.length;

    const prefix = input.slice(0, cutoff);
    const suffix = input.slice(cutoff);

    if (prefix in PathEnum) {
        const enumValue = PathEnum[prefix as keyof typeof PathEnum];
        if (typeof enumValue === 'string') {
            return `${enumValue}${suffix}`;
        }
    }

    return input;
};

export const buildLocalizedHref = (href: Href, basePath: string, locale: string): string => {
    const hrefString = resolveEnumPath(hrefToString(href));

    if (isExternalHref(hrefString)) {
        return hrefString;
    }

    const normalizedHref = normalizePath(hrefString);

    // Avoid double prefixing when href already contains base path and locale
    const expectedPrefix = `${basePath}/${locale}`.replace(/\/{2,}/g, '/');
    if (normalizedHref.startsWith(expectedPrefix)) {
        return normalizedHref;
    }

    // Avoid double prefixing when href already contains base path but lacks locale segment
    if (basePath && normalizedHref.startsWith(basePath)) {
        return `${expectedPrefix}${normalizedHref.slice(basePath.length)}`;
    }

    // Avoid double prefixing when href already starts with locale
    if (normalizedHref.startsWith(`/${locale}/`) || normalizedHref === `/${locale}`) {
        return `${basePath}${normalizedHref}`.replace(/\/{2,}/g, '/');
    }

    const localizedHref = `${basePath}/${locale}${normalizedHref}`;
    return localizedHref.replace(/\/{2,}/g, '/');
};

type CustomLinkProps = NextLinkProps &
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { locale?: string };

export const Link = forwardRef<HTMLAnchorElement, CustomLinkProps>(function Link(
    { href, locale, prefetch, replace, scroll, shallow, passHref, legacyBehavior, ...rest },
    ref,
) {
    const { basePath } = useFestivalContext();
    const currentLocale = useLocale();
    const targetLocale = locale ?? currentLocale;

    const resolvedHref = useMemo(
        () => buildLocalizedHref(href, basePath, targetLocale),
        [href, basePath, targetLocale],
    );

    if (isExternalHref(hrefToString(href))) {
        return <a ref={ref} href={hrefToString(href)} {...rest} />;
    }

    return (
        <NextLink
            ref={ref}
            href={resolvedHref}
            prefetch={prefetch}
            replace={replace}
            scroll={scroll}
            shallow={shallow}
            passHref={passHref}
            legacyBehavior={legacyBehavior}
            locale={false}
            {...rest}
        />
    );
});

type AppRouterInstance = ReturnType<typeof useNextRouter>;
type RouterPushOptions = Parameters<AppRouterInstance['push']>[1] & { locale?: string };
type RouterReplaceOptions = Parameters<AppRouterInstance['replace']>[1] & { locale?: string };
type RouterPrefetchOptions = Parameters<AppRouterInstance['prefetch']>[1] & { locale?: string };

export const useRouter = () => {
    const router = useNextRouter();
    const { basePath } = useFestivalContext();
    const currentLocale = useLocale();

    const buildHref = useCallback(
        (href: Href, locale?: string) =>
            buildLocalizedHref(href, basePath, locale ?? currentLocale),
        [basePath, currentLocale],
    );

    return useMemo(() => {
        const push = (href: Href, options?: RouterPushOptions) => {
            const { locale: localeOverride, ...rest } = options ?? {};
            router.push(buildHref(href, localeOverride), rest);
        };

        const replace = (href: Href, options?: RouterReplaceOptions) => {
            const { locale: localeOverride, ...rest } = options ?? {};
            router.replace(buildHref(href, localeOverride), rest);
        };

        const prefetch = (href: Href, options?: RouterPrefetchOptions) => {
            if (!options) {
                router.prefetch(buildHref(href));
                return;
            }
            const { locale: localeOverride, ...rest } = options;
            router.prefetch(buildHref(href, localeOverride), rest);
        };

        return {
            ...router,
            push,
            replace,
            prefetch,
        };
    }, [router, buildHref]);
};

const pathEnumEntries = Object.entries(PathEnum) as Array<[keyof typeof PathEnum, string]>;

export const usePathname = () => {
    const pathname = useNextPathname();
    const { basePath } = useFestivalContext();
    const locale = useLocale();

    if (!pathname) {
        return '';
    }

    let relativePath = pathname;

    if (basePath && relativePath.startsWith(basePath)) {
        relativePath = relativePath.slice(basePath.length);
    }

    if (!relativePath.startsWith('/')) {
        relativePath = `/${relativePath}`;
    }

    const localeSegment = `/${locale}`;
    if (relativePath.startsWith(localeSegment)) {
        relativePath = relativePath.slice(localeSegment.length);
    }

    if (!relativePath.startsWith('/')) {
        relativePath = `/${relativePath}`;
    }

    if (relativePath === '') {
        relativePath = '/';
    }

    let matchedKey: keyof typeof PathEnum | string = relativePath;
    let longestMatch = -1;

    for (const [key, value] of pathEnumEntries) {
        if (relativePath === value || relativePath.startsWith(value)) {
            if (value.length > longestMatch) {
                matchedKey = key;
                longestMatch = value.length;
            }
        }
    }

    return matchedKey;
};
